import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavConfig } from '../../context/NavConfigContext';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { NAV_CATEGORIES, NAV_FUNCTIONS } from '../../lib/navFunctions';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Logo } from '../common/Logo';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

export const Sidebar = ({ collapsed, mobileOpen, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPerm, user, logout, setUiLanguage } = useAuth();
  const { isPinned } = useNavConfig();
  const [sections, setSections] = useState(null);
  // Stato apri/chiudi delle categorie della sidebar, ricordato tra i reload.
  const [openCats, setOpenCats] = useLocalStorageState('vexel.sidebar.categories', {});
  // Default: CHIUSA. I gruppi funzioni si aprono solo manualmente (non all'avvio dell'app).
  const isCatOpen = (key) => openCats[key] === true;
  const toggleCat = (key) => setOpenCats((s) => ({ ...s, [key]: !(s[key] === true) }));

  const initials = (user?.name || '?').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  useEffect(() => {
    api.get('/sections').then(({ data }) => {
      const map = {};
      (data.sections || []).forEach((s) => { map[s.key] = s; });
      setSections(map);
    }).catch(() => setSections({}));
  }, []);

  const isActive = (path) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  // A function is visible if: user has permission AND section enabled AND role allowed.
  const isVisible = (item) => {
    if (!hasPerm(item.perm)) return false;
    if (!sections) return true; // before config loads, fall back to permission only
    const sec = sections[item.perm];
    if (!sec) return true;
    if (!sec.enabled) return false;
    return (sec.roles || []).includes(user?.role);
  };

  // Build the sidebar groups: per category, only PINNED + visible functions.
  // The Funzioni launcher is appended to the Management category.
  const groups = NAV_CATEGORIES.map((cat) => {
    const items = NAV_FUNCTIONS.filter((f) => f.cat === cat.key && isPinned(f.key) && isVisible(f));
    return { key: cat.key, sKey: cat.sKey, items };
  }).filter((g) => g.items.length > 0);

  // Render di un singolo item di navigazione (riusato in modalità estesa e compatta).
  const renderItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const label = t(item.tKey);
    return (
      <button
        key={item.path}
        data-testid={`nav-${item.path.slice(1)}`}
        onClick={() => { navigate(item.path); onCloseMobile && onCloseMobile(); }}
        title={collapsed ? label : undefined}
        className={cn(
          'w-full h-14 px-3.5 rounded-xl flex items-center gap-3.5 text-base font-semibold transition-colors',
          active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          collapsed && 'justify-center px-0'
        )}
      >
        <Icon className="h-6 w-6 shrink-0" />
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    );
  };

  const content = (
    <div className={cn('h-dvh bg-card border-r border-border flex flex-col transition-all duration-200', collapsed ? 'w-[88px]' : 'w-[300px]')}>
      <div className="h-[72px] flex items-center gap-3 px-4 border-b border-border">
        <Logo className="h-12 w-12 shrink-0 shadow-sm-token" rounded="rounded-2xl" />
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight truncate">{t('common.appName')}</div>
          </div>
        )}
        <button onClick={onCloseMobile} className="ml-auto md:hidden text-muted-foreground"><X className="h-6 w-6" /></button>
      </div>

      {!collapsed && user && (
        <div className="px-3 pt-3">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm-token">
            <div className="h-12 w-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center font-display font-extrabold text-base shrink-0">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-base leading-tight truncate">{user.name}</div>
              <div className="text-sm text-muted-foreground leading-tight">{t(`roles.${user.role}`, { defaultValue: user.role })}</div>
            </div>
            <span className="h-2.5 w-2.5 rounded-full bg-pos-free shrink-0" title={t('sidebar.online')} />
          </div>
        </div>
      )}

      <nav aria-label={t('sidebar.mainNav', { defaultValue: 'Navigazione principale' })} className="flex-1 overflow-y-auto no-scrollbar py-3 px-2.5 space-y-2">
        {groups.map((group) => {
          // In modalità compatta (sidebar ristretta) mostriamo solo le icone, senza tendine.
          if (collapsed) {
            return (
              <div key={group.key} className="space-y-1.5">
                {group.items.map(renderItem)}
              </div>
            );
          }
          const open = isCatOpen(group.key);
          return (
            <Collapsible key={group.key} open={open} onOpenChange={() => toggleCat(group.key)}>
              <CollapsibleTrigger
                data-testid={`sidebar-category-trigger-${group.key}`}
                aria-expanded={open}
                className="w-full flex h-11 items-center justify-between rounded-xl px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-colors"
              >
                <span className="truncate">{t(group.sKey)}</span>
                <ChevronDown
                  data-testid="sidebar-category-chevron"
                  className={cn('h-4 w-4 shrink-0 transition-transform duration-200 ease-out', open ? 'rotate-0' : '-rotate-90')}
                />
              </CollapsibleTrigger>
              <CollapsibleContent
                data-testid={`sidebar-category-content-${group.key}`}
                className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              >
                <div className="space-y-1.5 pt-1">
                  {group.items.map(renderItem)}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </nav>

      <div className="border-t border-border p-2.5 space-y-1.5">
        {!collapsed && (
          <div className="px-1 pb-1" data-testid="sidebar-language">
            <LanguageSwitcher className="w-full justify-start" onChange={(code) => setUiLanguage(code)} />
          </div>
        )}
        <button
          onClick={logout}
          data-testid="sidebar-logout"
          title={collapsed ? t('sidebar.logout') : undefined}
          className={cn('w-full h-14 px-3.5 rounded-xl flex items-center gap-3.5 text-base font-semibold text-destructive hover:bg-destructive/10 transition-colors', collapsed && 'justify-center px-0')}
        >
          <LogOut className="h-6 w-6 shrink-0" />
          {!collapsed && <span>{t('sidebar.logout')}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden md:block">{content}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onCloseMobile} />
          <div className="absolute left-0 top-0">{content}</div>
        </div>
      )}
    </>
  );
};
